export interface VeraMessage {
	role: 'user' | 'assistant';
	text: string;
	time: string;
	/** Quick-reply choices attached to this assistant message, if the backend sent any. */
	options?: Record<string, any>[];
	/** True once the user has tapped one of this message's options (buttons then disable). */
	optionsResolved?: boolean;
}

/** Backend may send a single option object or an array of them; always work with an array. */
export function normalizeOptions(raw: Record<string, any> | Record<string, any>[] | undefined): Record<string, any>[] | undefined {
	if (!raw) {
		return undefined;
	}
	return Array.isArray(raw) ? raw : [raw];
}

/**
 * The backend sometimes embeds the options payload as a fenced ```json block inside the
 * reply text instead of (or in addition to) the top-level `options` field. Strip that fence
 * out of the displayed text and parse its `options` out so it can render as buttons.
 */
export function extractOptions(text: string): { text: string; options?: Record<string, any>[] } {
	const fenceMatch = text?.match(/```(?:json)?\s*([\s\S]*?)```/i);
	if (!fenceMatch) {
		return { text };
	}

	try {
		const parsed = JSON.parse(fenceMatch[1].trim());
		// The fenced block may be `{ "options": [...] }` or the options array itself.
		const options = normalizeOptions(Array.isArray(parsed) ? parsed : parsed?.options);
		if (!options) {
			return { text };
		}
		const cleanedText = text.replace(fenceMatch[0], '').replace(/\n{3,}/g, '\n\n').trim();
		return { text: cleanedText, options };
	} catch {
		return { text };
	}
}

/** Best-effort display label for an option button: prefer a *_name/label/title field, else the first value. */
export function optionLabel(option: Record<string, any>): string {
	const preferredKeys = ['campaign_name', 'name', 'label', 'title'];
	for (const key of preferredKeys) {
		if (option?.[key]) {
			return String(option[key]);
		}
	}
	const firstValue = Object.values(option ?? {}).find((v) => v !== null && v !== undefined && v !== '');
	return firstValue !== undefined ? String(firstValue) : 'Option';
}

/** Minimal markdown-lite renderer: escapes HTML, then supports **bold** and line breaks. */
export function renderMessageText(text: string): string {
	return (text || '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
		.replace(/\n/g, '<br>');
}
